import React, { useState } from 'react';

export default function LineChart({ data, series, formatY, label }) {
  const [tooltip, setTooltip] = useState(null);

  if (!data || data.length < 2) {
    return (
      <div>
        {label && <h4 style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{label}</h4>}
        <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '24px 0', textAlign: 'center' }}>
          Not enough data points yet — check back after more sales
        </div>
      </div>
    );
  }

  const W = 600, H = 200;
  const padL = 56, padR = 16, padT = 12, padB = 32;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const allValues = data.flatMap(d => series.map(s => parseFloat(d[s.key]) || 0));
  const minY = Math.min(0, ...allValues);
  const maxY = Math.max(...allValues, 1);
  const rangeY = maxY - minY || 1;

  const toX = i => padL + (i / (data.length - 1)) * plotW;
  const toY = v => padT + plotH - ((v - minY) / rangeY) * plotH;

  const gridSteps = 4;
  const grids = Array.from({ length: gridSteps + 1 }, (_, i) => {
    const v = minY + (rangeY / gridSteps) * i;
    return { v, y: toY(v) };
  });

  // Decide which X labels to show
  const maxLabels = 7;
  const step = Math.ceil(data.length / maxLabels);

  return (
    <div>
      {label && <h4 style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{label}</h4>}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {/* Grid lines + Y labels */}
        {grids.map((g, i) => (
          <g key={i}>
            <line x1={padL} y1={g.y} x2={W - padR} y2={g.y}
              stroke="var(--border)" strokeWidth="0.5" />
            <text x={padL - 4} y={g.y + 4} textAnchor="end"
              fill="var(--text-muted)" fontSize="8">
              {formatY ? formatY(g.v) : g.v.toFixed(0)}
            </text>
          </g>
        ))}

        {/* Zero line */}
        {minY < 0 && (
          <line x1={padL} y1={toY(0)} x2={W - padR} y2={toY(0)}
            stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="3 2" />
        )}

        {/* X axis labels */}
        {data.map((d, i) => {
          if (i !== 0 && i !== data.length - 1 && i % step !== 0) return null;
          return (
            <text key={i} x={toX(i)} y={H - padB + 14} textAnchor="middle"
              fill="var(--text-muted)" fontSize="8">
              {d.date.slice(5)}
            </text>
          );
        })}

        {/* Series lines */}
        {series.map(s => {
          const pts = data.map((d, i) => `${toX(i).toFixed(1)},${toY(parseFloat(d[s.key]) || 0).toFixed(1)}`).join(' ');
          return (
            <polyline key={s.key} points={pts}
              fill="none" stroke={s.color} strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round" />
          );
        })}

        {/* Hover areas + dots */}
        {data.map((d, i) => {
          const slotW = plotW / data.length;
          return (
            <g key={i}>
              <rect
                x={toX(i) - slotW / 2} y={padT}
                width={slotW} height={plotH}
                fill="transparent"
                onMouseEnter={() => setTooltip({ i, d })}
                onMouseLeave={() => setTooltip(null)}
              />
              {tooltip?.i === i && series.map(s => (
                <circle key={s.key}
                  cx={toX(i)} cy={toY(parseFloat(d[s.key]) || 0)}
                  r="3" fill={s.color} />
              ))}
            </g>
          );
        })}

        {/* Tooltip */}
        {tooltip && (() => {
          const { i, d } = tooltip;
          const tipW = 130, tipH = series.length * 14 + 22;
          const tipX = Math.min(Math.max(toX(i) - tipW / 2, padL), W - padR - tipW);
          const tipY = padT + 4;
          return (
            <g style={{ pointerEvents: 'none' }}>
              <rect x={tipX} y={tipY} width={tipW} height={tipH}
                rx="4" fill="var(--bg-secondary)" stroke="var(--border)" strokeWidth="1" />
              <text x={tipX + 8} y={tipY + 13} fill="var(--text-secondary)" fontSize="9" fontWeight="600">
                {d.date}
              </text>
              {series.map((s, si) => (
                <g key={s.key}>
                  <rect x={tipX + 8} y={tipY + 19 + si * 14} width={6} height={6} rx="1" fill={s.color} />
                  <text x={tipX + 18} y={tipY + 26 + si * 14} fill="var(--text-secondary)" fontSize="9">
                    {s.label}: {formatY ? formatY(parseFloat(d[s.key]) || 0) : (parseFloat(d[s.key]) || 0)}
                  </text>
                </g>
              ))}
            </g>
          );
        })()}
      </svg>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, marginTop: 4 }}>
        {series.map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 18, height: 3, background: s.color, borderRadius: 2 }} />
            <span style={{ color: 'var(--text-secondary)' }}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
